import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

// 奖励项接口
export interface PostcardRewardItem {
  id?: number;
  item_type: string;
  product_id: number | null;
  product_name?: string;
  point_amount: number | null;
  reward_rate?: number;
  coupon_id: number | null;
  coupon_name?: string;
  quantity: number;
  sort_order: number;
  description?: string;
  _destroy?: boolean;
}

// 奖励配置接口
export interface PostcardRewardConfig {
  id: number;
  name: string;
  reward_type: string;
  rank_start: number | null;
  rank_end: number | null;
  threshold_count: number | null;
  min_water_multiplier: number;
  winner_take_highest?: boolean;
  sort_order: number;
  status: string;
  description?: string;
  items_count: number;
  items?: PostcardRewardItem[];
  created_at: string;
  updated_at: string;
}

// 发放记录接口
export interface PostcardRewardLog {
  id: number;
  user_id: number;
  user_nickname: string;
  user_mobile: string;
  reward_date: string;
  rank: number;
  postcard_count: number;
  config_id: number;
  config_name: string;
  config_description?: string;
  item_id: number;
  item_type: string;
  item_type_text: string;
  item_description?: string;
  status: string;
  status_text: string;
  retryable: boolean;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

// 发放统计接口
export interface PostcardRewardStats {
  date: string;
  total: number;
  by_status: {
    pending: number;
    delivered: number;
    failed: number;
  };
  by_item_type: {
    product: number;
    point: number;
    coupon: number;
  };
  by_config: Record<string, number>;
}

// 选项接口
export interface PostcardRewardOptions {
  products: Array<{ id: number; name: string; recycle_price: number }>;
  coupons: Array<{ id: number; name: string; coupon_type: string; discount_value: number; validity_days: number }>;
}

@Module
export default class PostcardRewardModule extends VuexModule {
  configs = [] as Array<PostcardRewardConfig>;
  config_count = 0;
  config = {} as PostcardRewardConfig;
  logs = [] as Array<PostcardRewardLog>;
  log_count = 0;
  log = {} as PostcardRewardLog;
  stats = {} as PostcardRewardStats;
  options = { products: [], coupons: [] } as PostcardRewardOptions;

  get currentPostcardRewardConfigs(): Array<PostcardRewardConfig> {
    return this.configs;
  }

  get currentPostcardRewardConfigCount(): number {
    return this.config_count;
  }

  get currentPostcardRewardConfig(): PostcardRewardConfig {
    return this.config;
  }

  get currentPostcardRewardLogs(): Array<PostcardRewardLog> {
    return this.logs;
  }

  get currentPostcardRewardLogCount(): number {
    return this.log_count;
  }

  get currentPostcardRewardLog(): PostcardRewardLog {
    return this.log;
  }

  get currentPostcardRewardStats(): PostcardRewardStats {
    return this.stats;
  }

  get currentPostcardRewardOptions(): PostcardRewardOptions {
    return this.options;
  }

  @Mutation
  [Mutations.SET_POSTCARD_REWARD_CONFIGS](configs: PostcardRewardConfig[]) {
    this.configs = configs;
  }

  @Mutation
  [Mutations.SET_POSTCARD_REWARD_CONFIG_COUNT](count: number) {
    this.config_count = count;
  }

  @Mutation
  [Mutations.SET_POSTCARD_REWARD_CONFIG](config: PostcardRewardConfig) {
    this.config = config;
  }

  @Mutation
  [Mutations.SET_POSTCARD_REWARD_LOGS](logs: PostcardRewardLog[]) {
    this.logs = logs;
  }

  @Mutation
  [Mutations.SET_POSTCARD_REWARD_LOG_COUNT](count: number) {
    this.log_count = count;
  }

  @Mutation
  [Mutations.SET_POSTCARD_REWARD_LOG](log: PostcardRewardLog) {
    this.log = log;
  }

  @Mutation
  [Mutations.SET_POSTCARD_REWARD_STATS](stats: PostcardRewardStats) {
    this.stats = stats;
  }

  @Mutation
  [Mutations.SET_POSTCARD_REWARD_OPTIONS](options: PostcardRewardOptions) {
    this.options = options;
  }

  // 获取配置列表
  @Action
  [Actions.GET_POSTCARD_REWARD_CONFIGS](params: Record<string, any>) {
    ApiService.setHeader();

    let queryString = "?";
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryString += "&" + key + "=" + params[key];
        }
      });
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/postcard_reward_configs", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_POSTCARD_REWARD_CONFIGS, data["configs"]);
          this.context.commit(Mutations.SET_POSTCARD_REWARD_CONFIG_COUNT, data["total"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  // 获取单个配置
  @Action
  [Actions.GET_POSTCARD_REWARD_CONFIG](id: number) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/postcard_reward_configs/${id}`)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_POSTCARD_REWARD_CONFIG, data["config"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  // 创建配置
  @Action
  [Actions.CREATE_POSTCARD_REWARD_CONFIG](params: Record<string, any>) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.post("admin/postcard_reward_configs", params)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_POSTCARD_REWARD_CONFIG, data["config"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  // 更新配置
  @Action
  [Actions.UPDATE_POSTCARD_REWARD_CONFIG](credentials: { id: number; values: Record<string, any> }) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.put(`admin/postcard_reward_configs/${credentials.id}`, credentials.values)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_POSTCARD_REWARD_CONFIG, data["config"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  // 删除配置
  @Action
  [Actions.DELETE_POSTCARD_REWARD_CONFIG](id: number) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.delete(`admin/postcard_reward_configs/${id}`)
        .then(() => {
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  // 获取选项（优惠券列表，商品改为单独搜索）
  @Action
  [Actions.GET_POSTCARD_REWARD_OPTIONS]() {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/postcard_reward_configs/options")
        .then(({ data }) => {
          this.context.commit(Mutations.SET_POSTCARD_REWARD_OPTIONS, {
            products: data["products"],
            coupons: data["coupons"]
          });
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  // 搜索商品（远程搜索）
  @Action
  [Actions.SEARCH_POSTCARD_REWARD_PRODUCTS](keyword: string) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.query("admin/postcard_reward_configs/search_products", { params: { keyword } })
        .then(({ data }) => {
          this.context.commit(Mutations.SET_POSTCARD_REWARD_OPTIONS, {
            products: data["products"],
            coupons: this.options.coupons // 保留原有优惠券数据
          });
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  // 获取发放记录列表
  @Action
  [Actions.GET_POSTCARD_REWARD_LOGS](params: Record<string, any>) {
    ApiService.setHeader();

    let queryString = "?";
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryString += "&" + key + "=" + params[key];
        }
      });
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/postcard_reward_logs", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_POSTCARD_REWARD_LOGS, data["logs"]);
          this.context.commit(Mutations.SET_POSTCARD_REWARD_LOG_COUNT, data["total"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  // 获取发放统计
  @Action
  [Actions.GET_POSTCARD_REWARD_STATS](params: { date?: string }) {
    ApiService.setHeader();

    let queryString = "";
    if (params?.date) {
      queryString = `?date=${params.date}`;
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/postcard_reward_logs/stats", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_POSTCARD_REWARD_STATS, {
            date: data["date"],
            total: data["stats"]["total"],
            by_status: data["stats"]["by_status"],
            by_item_type: data["stats"]["by_item_type"],
            by_config: data["stats"]["by_config"]
          });
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  // 手动发放奖励
  @Action
  [Actions.DISTRIBUTE_POSTCARD_REWARDS](params: { date: string; force?: boolean }) {
    ApiService.setHeader();

    return new Promise<any>((resolve, reject) => {
      ApiService.post("admin/postcard_reward_logs/distribute", params)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.error || '请求失败');
          reject(response?.data);
        });
    });
  }

  // 重试失败的发放
  @Action
  [Actions.RETRY_FAILED_POSTCARD_REWARDS](params: { date?: string }) {
    ApiService.setHeader();

    return new Promise<any>((resolve, reject) => {
      ApiService.post("admin/postcard_reward_logs/retry_failed", params)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.error || '请求失败');
          reject(response?.data);
        });
    });
  }

  // 重试单条发放记录
  @Action
  [Actions.RETRY_POSTCARD_REWARD_LOG](id: number) {
    ApiService.setHeader();

    return new Promise<any>((resolve, reject) => {
      ApiService.post(`admin/postcard_reward_logs/${id}/retry`, {})
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.error || '请求失败');
          reject(response?.data);
        });
    });
  }
}
